import { useEffect, useState } from 'react';
import { useCurrentCompany } from '../../../hooks/useCompany';
import http from '../../../api/axios';
import { apiUrl } from '../../../api/apiUrl';
import DataTable from '../../../components/table/DataTable';
import { columns } from '../../../components/columns/Company/FilesColumns';
import { toast } from 'react-toastify';
import { onDownloadItem } from '../../../utils/functions/file.functions';
import { FileUpload } from '../../../components/files/FileUpload';
import { AttachmentType, FileBase } from '../../../types/Files';
import { useTranslation } from '../../../context/translationContext';
import { Modal } from '../../../components/modal/modal';
import FilePreview from '../../../components/filePreview/FilePreview';
import { useLoading } from '../../../context/loadingContext';
import { supportedFileType } from '../../../utils/functions/data.functions';

interface Props {
  entityId: string;
  entityType: string;
  files: FileBase[] | null;
  setFiles: React.Dispatch<React.SetStateAction<FileBase[] | null>>;
  fetchFiles: () => void;
}

/**
 * Invoice side panel for chat and file table
 */
export const InvoiceSidePanelTabFiles = ({ entityId, entityType, files, fetchFiles }: Props) => {
  const { t } = useTranslation();
  const { company } = useCurrentCompany();
  const [selectedId, setSelectedId] = useState<string>();
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  // for refreshing file data after export or saving document
  const { loading, startLoading, stopLoading } = useLoading();

  useEffect(() => {
    if (loading) return;
    fetchFiles();
  }, [loading]);

  /**
   * Removes selected file
   * @param id of entity
   */
  const onRemoveItem = async (id: string | number) => {
    if (id === null) return;
    const response = await http.apiDelete({
      url: `${apiUrl.FILE_DELETE}?id=${id}`,
    });
    if (response?.status === 204) {
      toast.success('Success!');
      fetchFiles();
    }
  };

  /**
   * Opens preview of file
   */
  const handleOpenPreview = (id: string) => {
    setSelectedId(id);
    setPreviewOpen(true);
  };

  return (
    <>
      <div className="flex justify-between mb-5">
        <FileUpload
          apiPostFilesUrl={`${apiUrl.FILE_UPLOAD_AND_ASSIGN}?companyId=${company?.id}&entityType=${entityType}&attachmentType=${AttachmentType.OTHER}&entityId=${entityId}`}
          onUpload={() => fetchFiles()}
        />
      </div>
      <DataTable
        data={files}
        columns={columns}
        handleRemove={onRemoveItem}
        showContextMenu
        customButtons={[
          {
            title: t('general.preview'),
            color: 'blue',
            onSubmit: (e) => handleOpenPreview(e.toString()),
            renderCondition: (e) => {
              const contentType = files?.find((f: FileBase) => f.id === e)?.contentType;
              if (contentType) return supportedFileType(contentType);
              return false;
            },
          },
          {
            title: t('general.download'),
            color: 'blue',
            onSubmit: (e) => {
              startLoading();
              onDownloadItem(
                `${apiUrl.FILE_DOWNLOAD}?id=${e}`,
                files?.find((f: FileBase) => f.id === e)?.name,
              ).then(() => stopLoading());
            },
          },
        ]}
      />
      {selectedId && (
        <Modal
          open={previewOpen}
          title={files?.find((f: FileBase) => f.id === selectedId)?.name ?? 'Preview'}
          className={`min-w-[100%] min-h-screen !m-0 !rounded-none`}
          onClose={() => setPreviewOpen(false)}
        >
          <FilePreview id={selectedId} />
        </Modal>
      )}
    </>
  );
};
